import React, { useEffect, useMemo, useRef } from "react";

import { AgGridOverlay } from "@src/components/overlays/ag_grid_overlay/index";
import { REQUEST_STATUS, RESOURCE_PLAN_TABS } from "@src/constants";
import { AgGridProvider } from "@src/context/use_ag_grid_context";
import { ResourcePlanToolBar } from "@src/features/resource_plan_v2/components/resource_plan_tool_bar";
import UnsavedChangesModal from "@src/features/resource_plan_v2/components/unsaved_changes_modal/unsaved_changes_modal";
import { useResourcePlanStateHandler } from "@src/features/resource_plan_v2/hooks/use_resource_plan_state_change_handler";
import {
  createRequestPayload,
  getAllocationChangesForRequest,
  mapRoleRequestData,
} from "@src/features/resource_plan_v2/utils/resource_plan_table_utils";
import {
  hasTableDataChanged,
  isDraftOptionDisabled,
  isSendOptionDisabled,
  isUndoOptionDisabled,
  isRequestWithoutTeamLead,
  isUpdatedValueNotInRange,
  getAllocationsOfRequest,
  hasEmptyRequest,
  hasChanges,
} from "@src/features/resource_plan_v2/utils/table_actions_utils";
import { RoleRequestDetailsModal } from "@src/features/role_request_details/components/role_request_details_modal";
import { toggleErrorFlagInContext } from "@src/features/staffing_request_details/utils/propose_allocations_utils";
import { ActiveFilterBar } from "@src/features/table_filtering/components/active_filter_bar/index";
import {
  setDraftActionDisabled,
  setInboxActionDisabled,
  setIsSendRequestOptionDisabled,
  setIsTableDataChanged,
  setIsUndoRequestOptionDisabled,
  setPublishRequestOptionDisabled,
  setHasSelectedDraftRequestsError,
  setIsSelectedInboxRequestsUnchanged,
  setHasSelectedInboxRequestsError,
} from "@src/services/resourcePlanTableActionsSlice";
import {
  useGetRoleRequestsByProjectIdQuery,
  useUpdateRoleRequestMutation,
} from "@src/services/slices/projectsApi";
import { RootState, useAppDispatch, useAppSelector } from "@src/setupStore";
import {
  AllocationRowData,
  ResourcePlanTableRowData,
  RoleAllocationDetailsDTO,
  RoleRequest,
  UpdateRoleRequestPayload,
} from "@src/types/role_request_types";
import { isValueValid } from "@src/utils/edit_allocation_utils";
import {
  ColDef,
  FilterChangedEvent,
  GridOptions,
  IRowNode,
} from "ag-grid-community";
import type { CellValueChangedEvent } from "ag-grid-community/dist/types/src/events";
import { AgGridReact } from "ag-grid-react";
import { App } from "antd";
import _ from "lodash";

import {
  getColumnDefs,
  getGridOptions,
} from "../../utils/resource_plan_table_grid_options";

import "./resource_plan_table.less";

interface ResourcePlanTableProps {
  projectId: string;
  projectEndDate: Date;
}

const ResourcePlanTable: React.FC<ResourcePlanTableProps> = ({
  projectId,
  projectEndDate,
}) => {
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const didInitRef = useRef(false);
  const gridRef = useRef(null);

  const [roleRequestRowData, setRoleRequestRowData] = React.useState<
    ResourcePlanTableRowData[]
  >([]);

  const {
    jointVenturePartner,
    isProjectEnded,
    resourcePlanTableState: { currentTabConfig, isTableDisabled },
  } = useAppSelector((state: RootState) => state.resourcePlanSliceV2);

  const gridOptions = useMemo<GridOptions>(() => {
    return getGridOptions();
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    return getColumnDefs(projectEndDate);
  }, [projectEndDate]);

  const { data: roleRequestData, isFetching } =
    useGetRoleRequestsByProjectIdQuery(projectId);
  const [updateRoleRequestMutation] = useUpdateRoleRequestMutation();

  /**
   * This useEffect was needed because everytime the cache after the cache was revalidated the table lost it`s state.
   * This happens when 2 requests were updated but only one send than the cache for the send one was revalidating
   * and the ag grid table rerenders the table with old data so that the second not send changes with the highlighting.
   */
  useEffect(() => {
    if (!isFetching && roleRequestData && !didInitRef.current) {
      setRoleRequestRowData(
        mapRoleRequestData(roleRequestData, jointVenturePartner)
      );
      didInitRef.current = true;
    }
  }, [roleRequestData, isFetching]);

  const { activeFilters, resetActiveFilters, setTableRendered } =
    useResourcePlanStateHandler({
      gridApi: gridRef?.current?.api,
      columnDefs,
    });

  /**
   * AG Grid Filter Changed event to show/hide no rows overlay
   *
   * @param event
   */
  function onFilterChanged(event: FilterChangedEvent): void {
    const displayedRowCount: number = event.api.getDisplayedRowCount();

    if (displayedRowCount === 0 && !isProjectEnded) {
      event.api.showNoRowsOverlay();
    } else {
      event.api.hideOverlay();
    }
  }

  /**
   * Selects rows in the AG Grid where any allocation has a valid `updatedPercentage`.
   *
   * This function iterates over all grid rows and selects those where at least one allocation
   * has a non-null, valid `updatedPercentage`.
   */
  function handleRowSelection(
    params: CellValueChangedEvent<ResourcePlanTableRowData>
  ) {
    const { data } = params.node;

    if (data.status === REQUEST_STATUS.DRAFT) return;

    // At least one allocation has a valid updatedPercentage.
    const shouldSelect: boolean = [...params.data.allocations.values()].some(
      (allocation: AllocationRowData) =>
        isValueValid(allocation.updatedPercentage)
    );

    params.node.setSelected(shouldSelect);
  }

  /**
   * Iterates over grid nodes and updates draft requests that have allocation changes.
   */
  function handleDraftUpdate() {
    if (!gridRef?.current?.api) return;

    gridRef.current.api.forEachNode(
      (node: IRowNode<ResourcePlanTableRowData>) => {
        const request: ResourcePlanTableRowData = node.data;

        const newAllocations = getAllocationChangesForRequest(request);
        if (newAllocations.length === 0) return;

        updateRoleRequest(request, newAllocations);
      }
    );
  }

  /**
   * Sends an update for a draft role request with the new allocation changes.
   *
   * @param request - The resource plan request data.
   * @param newAllocations - The list of updated allocation details.
   */
  async function updateRoleRequest(
    request: ResourcePlanTableRowData,
    newAllocations: RoleAllocationDetailsDTO[]
  ) {
    const updateRequestPayload: UpdateRoleRequestPayload = createRequestPayload(
      request,
      newAllocations,
      true
    );

    const roleRequest: RoleRequest = await updateRoleRequestMutation({
      projectId: projectId,
      roleRequestId: request.roleRequestId.toString(),
      requestPayload: updateRequestPayload,
    }).unwrap();

    const mappedRoleRequest = mapRoleRequestData([roleRequest]);
    gridRef?.current?.api.applyTransaction({ update: mappedRoleRequest });

    updateTableActionState();
  }

  /**
   * Handles cell value changes in the AG Grid.
   *
   * - `handleRowSelection` is called immediately.
   * - `handleDraftUpdate` is debounced to prevent excessive updates.
   *
   * @param {CellValueChangedEvent<ResourcePlanTableRowData>} event - The event object containing details about the changed cell.
   */
  function onCellValueChanged(
    event: CellValueChangedEvent<ResourcePlanTableRowData>
  ) {
    toggleErrorFlagInContext(event, message);
    handleRowSelection(event);
    if (event.data.status === REQUEST_STATUS.DRAFT) {
      debouncedHandleDraftUpdate();
    }
  }

  /**
   * Debounced version of `handleDraftUpdate` to reduce frequent updates.
   */
  const debouncedHandleDraftUpdate = _.debounce(() => {
    handleDraftUpdate();
  }, 10);

  /**
   * Updates the Redux store with the currently selected requests.
   *
   */
  function updateTableActionState(): void {
    const currentTab: string = currentTabConfig.key;

    switch (currentTab) {
      case RESOURCE_PLAN_TABS.DRAFTS:
        updateDraftActionState();
        break;
      case RESOURCE_PLAN_TABS.RESOURCE_PLAN_INBOX:
        updateInboxActionState();
        break;
      default:
        break;
    }
  }

  /**
   * Updates the Redux store with the state of the inbox action options.
   */
  function updateInboxActionState(): void {
    const selectedSendRequests: ResourcePlanTableRowData[] =
      gridRef?.current?.api
        ?.getSelectedRows()
        .filter(
          (request: ResourcePlanTableRowData) =>
            request.status !== REQUEST_STATUS.DRAFT
        );

    const isInboxActionDisabled: boolean = selectedSendRequests.length === 0;
    const isUndoRequestDisabled: boolean =
      isUndoOptionDisabled(selectedSendRequests);
    const isSendRequestsDisabled: boolean =
      isSendOptionDisabled(selectedSendRequests);
    const isTableDataChanged: boolean = hasTableDataChanged(
      getAllDisplayedRows()
    );

    const allocations: AllocationRowData[] =
      getAllocationsOfRequest(selectedSendRequests);

    const hasErrorsForSelectedRequests: boolean =
      isRequestWithoutTeamLead(selectedSendRequests) ||
      isUpdatedValueNotInRange(allocations);
    const isRequestUpdatedAndNotEmpty: boolean =
      hasEmptyRequest(allocations) || !hasChanges(allocations);

    dispatch(setHasSelectedInboxRequestsError(hasErrorsForSelectedRequests));
    dispatch(setInboxActionDisabled(isInboxActionDisabled));
    dispatch(setIsUndoRequestOptionDisabled(isUndoRequestDisabled));
    dispatch(setIsSendRequestOptionDisabled(isSendRequestsDisabled));
    dispatch(setIsSelectedInboxRequestsUnchanged(isRequestUpdatedAndNotEmpty));
    dispatch(setIsTableDataChanged(isTableDataChanged));
  }

  function getAllDisplayedRows(): ResourcePlanTableRowData[] {
    const rows: ResourcePlanTableRowData[] = [];
    gridRef.current?.api.forEachNode((node) => rows.push(node.data));
    return rows;
  }

  /**
   * Updates the Redux store with the state of the draft action buttons.
   */
  function updateDraftActionState(): void {
    const selectedDraftRequests: ResourcePlanTableRowData[] =
      gridRef?.current?.api
        .getSelectedRows()
        .filter(
          (request: ResourcePlanTableRowData) =>
            request.status === REQUEST_STATUS.DRAFT
        );

    const isPublishRequestDisabled: boolean = isDraftOptionDisabled(
      selectedDraftRequests
    );
    const isDraftActionSelectionDisabled: boolean =
      selectedDraftRequests.length === 0;

    const allocations: AllocationRowData[] = getAllocationsOfRequest(
      selectedDraftRequests
    );
    const hasErrorsForSelectedRequests: boolean =
      isRequestWithoutTeamLead(selectedDraftRequests) ||
      isUpdatedValueNotInRange(allocations);

    dispatch(setHasSelectedDraftRequestsError(hasErrorsForSelectedRequests));
    dispatch(setPublishRequestOptionDisabled(isPublishRequestDisabled));
    dispatch(setDraftActionDisabled(isDraftActionSelectionDisabled));
  }

  return (
    <AgGridProvider gridRef={gridRef}>
      <div className="resource-plan-table" data-testid="resource-plan-table">
        <UnsavedChangesModal />
        <RoleRequestDetailsModal />
        <ResourcePlanToolBar
          isFetching={isFetching}
          isDisabled={isTableDisabled}
          roleRequestData={roleRequestData}
          columnDefs={columnDefs}
          projectId={projectId}
          isProjectEnded={isProjectEnded}
        />

        <ActiveFilterBar
          activeFilters={activeFilters}
          onRemove={(key: string, value: string) =>
            resetActiveFilters(key, value)
          }
          onRemoveAll={() => resetActiveFilters()}
          isDisabled={isTableDisabled}
        />
        <div
          data-testid="resource-plan-table-container"
          className={`resource-plan-table__table-container ${
            isProjectEnded && !isFetching ? "project-end-notification" : ""
          }`}
          style={{ height: 400 }}
        >
          <AgGridReact
            ref={gridRef}
            rowData={roleRequestRowData}
            gridOptions={gridOptions}
            noRowsOverlayComponent={AgGridOverlay}
            noRowsOverlayComponentParams={{
              text: "No requests found",
              customStyle: { marginTop: "48px" },
            }}
            loadingOverlayComponent={AgGridOverlay}
            loadingOverlayComponentParams={{
              text: "Loading requests...",
              customStyle: { marginTop: "48px" },
            }}
            loading={isFetching}
            onSelectionChanged={updateTableActionState}
            onFilterChanged={onFilterChanged}
            onCellValueChanged={onCellValueChanged}
            onCellEditingStopped={updateTableActionState}
            onFillEnd={updateTableActionState}
            columnDefs={getColumnDefs(projectEndDate)}
            onFirstDataRendered={setTableRendered}
          />
        </div>
      </div>
    </AgGridProvider>
  );
};

export default React.memo(ResourcePlanTable);
